/**
 * The docking autopilot (M2 spec §7.2 step 2): flies a ship from anywhere within auto-dock range into the bay and
 * holds it over the pad, through the real flight model. It only writes a `ControlInput` (surge, sway, heave,
 * pitch, yaw, roll), exactly as the AI pilots do (D-013): no teleports, no forces, no shortcuts.
 *
 * Guidance is a cascade of three simple loops:
 * - **Position → desired world velocity**, per phase (below). `autopilot.lateralGain` (1/s) is the one position
 *   gain: every speed profile is `gain × distance` clamped between the phase's floor and cap, so the braking the
 *   profile asks for never exceeds `gain × speed` (45 m/s² at 90 m/s with the stock gain, the Kestrel's lateral
 *   thrust).
 * - **Desired velocity → translation stick.** With flight assist on, a stick deflection sets a target local velocity
 *   of `stick × maxSpeed` (src/flight/flightModel.ts), so surge, sway and heave are the ship-local desired velocity
 *   divided by `maxSpeed`. FA must be on and boost off: the runtime (M2-14) forces FA on during auto-dock, restores
 *   it afterwards and never boosts.
 * - **Attitude.** P control on the nose error (a rotation vector in the ship frame, `turnGain` per radian) and on
 *   the roll error against the bay's up (`rollGain` per radian), each stick clamped to ±1: the AI pilots' convention.
 *
 * Phases (`AUTODOCK_PHASE`), re-evaluated every step:
 * - **detour**: the straight line to the entry point passes within the clearance sphere (`stationRadius +
 *   stationMargin` around `stationPos`). Fly the tangent of that sphere round the station, in the plane of the
 *   station centre, the ship and the entry point, at the leg speed (inside the sphere: out and round). The
 *   detour ends the moment the line to the entry point clears.
 * - **entry**: fly straight to the entry point, `entryDistance` out along the approach axis from the mouth (pushed
 *   further out if it would sit inside the clearance sphere), at the leg speed, slowing as `gain × distance`.
 * The leg speed (`legSpeed`) is `detourSpeed` beyond `detourNear` of the entry point and the approach tube, and
 * `approachSpeed` within it (scale build SC-5a1b: a far-side detour round a km station is kilometres long).
 * - **corridor** (inside the approach tube, or within `arriveRadius` of the entry point): along the axis toward the
 *   mouth at `corridorSpeed`, slowing to `baySpeed` at the mouth, with the lateral error driven to zero at
 *   `gain × error`. The ship only crosses the mouth once its hull fits the mouth: the lateral error plus the hull's
 *   half extent across the bay at its current attitude (`hullHalfAlong` of `def.hullHalfExtents`) keeps
 *   `hullMargin` to each wall; until then it holds `arriveRadius` in front of the mouth.
 * - **bay** (past the mouth plane): toward the pad at `baySpeed`, falling as `gain × distance` to `finalSpeed`.
 * - **hold** (within `finalSpeed / gain` of the pad): stop over the pad (`gain × distance`, capped at `finalSpeed`).
 * In detour and entry the nose leads along the desired velocity; from the corridor on it points into the bay
 * (−out). The roll always follows the bay's up.
 *
 * Results: `arrived` while in the bay or hold phase with every dock limit met (in the bay, under `dock.maxSpeed`,
 * nose and roll within `dock.maxAlign`; the runtime's `stepDockHold` then completes the dock), `failed` when the
 * ship cannot dock at all (its hull cross-section plus `hullMargin` to each wall does not fit the mouth even lined
 * up with the bay, or it is longer than the bay is deep, or its state is not finite; the control is zeroed, so FA
 * brings it to rest), else `running`. The cross-section is the hull's box, not a bounding sphere: a sphere holds the
 * hull's length too, which shut every M hull out of the 44 × 26 m M bay (D-999).
 *
 * **The hull box, not the origin, is what flies** (D-TBD): every phase steers the centre of the asset's bounds box
 * (`def.hullCentre`, turned by the ship's attitude), so the box is centred on the bay axis in the corridor and on the
 * pad in the bay, and the docked and launch poses (`originForHullCentre`) do the same. It is the box the asset
 * pipeline's `bayFit` check turns, and `hullMargin` is the margin bayFit uses (`budgets.json` takes it from
 * `docking.json`), so the two accept and refuse the same hulls. Aiming the origin instead left an off-centre hull
 * (the cutter's bounds centre is 4 m above its origin) 2.4 m from the ceiling.
 *
 * `stationRadius` must bound the whole station collider around `stationPos` (the runtime passes the model's
 * bounds radius plus the distance from the station origin to the bounds centre). The approach tube is assumed
 * clear (the asset contract for stations: nothing in front of the mouth along the corridor).
 *
 * Every function here runs per tick: allocation-free, module scratch only (D-014). Positions are float64 world
 * metres (D-011).
 */
import { clamp, inverseRotateVec, rotateVec, rotateVecFrom, Vec3Scratch, type Quat, type Vec3 } from '../core/math'
import type { DockingConfig } from '../data/m2Schemas'
import type { ShipDef } from '../data/schemas'
import type { ControlInput } from '../flight/flightModel'
import type { BayFrame } from '../space/contracts'
import { bayLocalOf, pointInBayOf } from './bay'
import { corridorRadius, FloatScratch, noseErrorTo, rollErrorTo } from './corridor'

export const AUTODOCK_PHASE = { detour: 0, entry: 1, corridor: 2, bay: 3, hold: 4 } as const
export type AutodockPhase = (typeof AUTODOCK_PHASE)[keyof typeof AUTODOCK_PHASE]

export type AutodockResult = 'running' | 'arrived' | 'failed'

/** Per-ship autopilot state (the runtime keeps one for the player; `resetAutodock` when it (re)starts). */
export interface AutodockState {
  phase: AutodockPhase
  /** Seconds of autopilot since the last reset. */
  elapsed: number
  /** The point the current phase steers for (tangent point, entry point, mouth or pad), float64 world. */
  target: Vec3
  /** The entry point on the approach axis, float64 world. */
  entry: Vec3
  /** The desired world velocity of the last step (m/s). */
  desiredVel: Vec3
}

/** What the autopilot reads of the ship: float64 world position, world velocity and rotation. */
export interface AutodockShip {
  pos: Readonly<Vec3>
  vel: Readonly<Vec3>
  rot: Readonly<Quat>
}

export type AutodockDef = Pick<ShipDef, 'maxSpeed' | 'hullHalfExtents' | 'hullCentre'>
export type AutodockConfig = Pick<DockingConfig, 'autopilot' | 'corridor' | 'dock'>

/** Below this a direction is degenerate. */
const EPS = 1e-9

export function createAutodockState(): AutodockState {
  return {
    phase: AUTODOCK_PHASE.entry,
    elapsed: 0,
    target: new Vec3Scratch(),
    entry: new Vec3Scratch(),
    desiredVel: new Vec3Scratch(),
  }
}

/** Back to the start: the next step picks its phase from scratch. */
export function resetAutodock(state: AutodockState): void {
  state.phase = AUTODOCK_PHASE.entry
  state.elapsed = 0
  state.desiredVel.x = 0
  state.desiredVel.y = 0
  state.desiredVel.z = 0
}

/**
 * The per-tick floats of one autopilot step (DA-1, D-TBD; D-1052's `TurretFloats`). `stepAutodock` runs past V8's
 * cumulative inlining budget, so a float passed to or returned from a callee V8 does not inline is boxed (16 B each).
 * Its callees take this number-initialised class instead (the `= 0` initialisers matter: bare declarations read 816 B,
 * P5a11b), so only objects, small integers and booleans cross a call.
 */
export class AutodockFloats {
  /** `stationRadius + autopilot.stationMargin`: the clearance sphere round the station. */
  clearance = 0
  /** `entryAlongFrom`'s result. */
  entryAt = 0
  /** `autopilot.lateralGain`. */
  gain = 0
  /** The detour leg speed. */
  leg = 0
  /** `approachFrom`'s speed floor and cap. */
  lo = 0
  hi = 0
  turnGain = 0
  rollGain = 0
  maxSpeed = 0
  /** `steerAttitudeFrom`'s nose error rotation (pitch and yaw axes): one store per branch, never a phi with 0 (D-1051). */
  wx = 0
  wy = 0
}

/** A station as the autopilot reads it: float64 world centre and the radius bounding its collider. */
export interface AutodockStation {
  pos: Readonly<Vec3>
  radius: number
}

class StationScratch implements AutodockStation {
  pos: Readonly<Vec3> = new Vec3Scratch()
  radius = 0
}

// Module scratch (D-014).
const loc: Vec3 = new Vec3Scratch()
const tmp: Vec3 = new Vec3Scratch()
const fwd: Vec3 = new Vec3Scratch()
const up: Vec3 = new Vec3Scratch()
const centre: Vec3 = new Vec3Scratch()
/** The world velocity the stick commands on a moving frame: the desired velocity relative to the bay plus the bay's (F19). */
const worldVel: Vec3 = new Vec3Scratch()
/** The desired nose direction (unit world). */
const nose: Vec3 = new Vec3Scratch()
/** A quaternion scratch with its own map, written only here with floats (see `inverseRotateFrom`). */
class ConjScratch implements Quat {
  x = 0
  y = 0
  z = 0
  w = 0
}
/** The conjugate of the ship's rotation: `rotateVecFrom` with it is `inverseRotateVec`, bit for bit. */
const conj = new ConjScratch()
const hullC: Vec3 = new Vec3Scratch()
const result = new FloatScratch()
/** `stepAutodock`'s floats, and the float-argument wrappers' (kept apart so a wrapper never clobbers a step). */
const floats = new AutodockFloats()
const wrapFloats = new AutodockFloats()
const wrapA: Vec3 = new Vec3Scratch()
const wrapB: Vec3 = new Vec3Scratch()
const wrapStation = new StationScratch()

/** `inverseRotateVec(q, v.x, v.y, v.z, out)` with only objects crossing the call (the same arithmetic, bit for bit). */
function inverseRotateFrom(q: Readonly<Quat>, v: Readonly<Vec3>, out: Vec3): Vec3 {
  conj.x = -q.x
  conj.y = -q.y
  conj.z = -q.z
  conj.w = q.w
  return rotateVecFrom(conj, v, out)
}

/**
 * Distance of the entry point from the mouth along the out axis, into `k.entryAt`: `autopilot.entryDistance`, or
 * further out when that point (plus `arriveRadius`) would lie inside the clearance sphere of radius `k.clearance`
 * round `stationPos`.
 */
export function entryAlongFrom(
  frame: Readonly<BayFrame>,
  stationPos: Readonly<Vec3>,
  k: AutodockFloats,
  cfg: Pick<DockingConfig, 'autopilot'>,
): void {
  const clearance = k.clearance
  const o = frame.out
  const mx = frame.mouth.x - stationPos.x
  const my = frame.mouth.y - stationPos.y
  const mz = frame.mouth.z - stationPos.z
  // |mouth + out·a − station|² = clearance² → a² + 2ab + c = 0; the far root is where the axis leaves the sphere.
  const b = o.x * mx + o.y * my + o.z * mz
  const c = mx * mx + my * my + mz * mz - clearance * clearance
  const disc = b * b - c
  let leave = 0
  if (disc > 0) {
    const far = -b + Math.sqrt(disc)
    if (far > 0) leave = far
  }
  const needed = leave + cfg.autopilot.arriveRadius
  if (needed > cfg.autopilot.entryDistance) k.entryAt = needed
  else k.entryAt = cfg.autopilot.entryDistance
}

/** `entryAlongFrom` with the clearance as a float (the same arithmetic): for callers off the per-tick path. */
export function entryAlong(
  frame: Readonly<BayFrame>,
  stationPos: Readonly<Vec3>,
  clearance: number,
  cfg: Pick<DockingConfig, 'autopilot'>,
): number {
  wrapFloats.clearance = clearance
  entryAlongFrom(frame, stationPos, wrapFloats, cfg)
  return wrapFloats.entryAt
}

/** True when some point of the segment `a` → `b` lies closer than `k.clearance` to `c`. */
export function segmentBlockedFrom(a: Readonly<Vec3>, b: Readonly<Vec3>, c: Readonly<Vec3>, k: Readonly<AutodockFloats>): boolean {
  const ax = a.x
  const ay = a.y
  const az = a.z
  const radius = k.clearance
  const dx = b.x - ax
  const dy = b.y - ay
  const dz = b.z - az
  const fx = ax - c.x
  const fy = ay - c.y
  const fz = az - c.z
  const len2 = dx * dx + dy * dy + dz * dz
  const t = len2 > EPS ? clamp(-(fx * dx + fy * dy + fz * dz) / len2, 0, 1) : 0
  const qx = fx + dx * t
  const qy = fy + dy * t
  const qz = fz + dz * t
  return qx * qx + qy * qy + qz * qz < radius * radius
}

/** True when some point of the segment (ax, ay, az) → (bx, by, bz) lies closer than `radius` to `c`. */
export function segmentBlocked(
  ax: number,
  ay: number,
  az: number,
  bx: number,
  by: number,
  bz: number,
  c: Readonly<Vec3>,
  radius: number,
): boolean {
  wrapA.x = ax
  wrapA.y = ay
  wrapA.z = az
  wrapB.x = bx
  wrapB.y = by
  wrapB.z = bz
  wrapFloats.clearance = radius
  return segmentBlockedFrom(wrapA, wrapB, c, wrapFloats)
}

/**
 * The float64 world position of the hull box's centre (`ShipDef.hullCentre`, ship space) for a ship whose origin is at
 * `pos` with rotation `rot`, into `out` (`out` must not alias `pos`). Allocation-free; only objects cross the rotation.
 */
export function hullCentreAt(pos: Readonly<Vec3>, rot: Readonly<Quat>, c: Readonly<[number, number, number]>, out: Vec3): Vec3 {
  hullC.x = c[0]
  hullC.y = c[1]
  hullC.z = c[2]
  rotateHullInto(rot, hullC, out)
  out.x += pos.x
  out.y += pos.y
  out.z += pos.z
  return out
}

/**
 * `hullCentreAt`'s rotation: `rotateVecFrom`'s expression (bit for bit) in a private copy, so its feedback is the
 * autopilot's alone. Inlined with `rotateVecFrom`'s game-wide feedback it boxed 2 floats per tick once traffic had
 * docked several hulls; writing it out in `hullCentreAt` instead cost the station corridor case the inlining budget
 * (16–32 B/tick in about half the runs) (DA-1 follow-up, D-TBD).
 */
function rotateHullInto(q: Readonly<Quat>, v: Readonly<Vec3>, out: Vec3): void {
  const qx = q.x
  const qy = q.y
  const qz = q.z
  const qw = q.w
  const vx = v.x
  const vy = v.y
  const vz = v.z
  const tx = 2 * (qy * vz - qz * vy)
  const ty = 2 * (qz * vx - qx * vz)
  const tz = 2 * (qx * vy - qy * vx)
  out.x = vx + qw * tx + (qy * tz - qz * ty)
  out.y = vy + qw * ty + (qz * tx - qx * tz)
  out.z = vz + qw * tz + (qx * ty - qy * tx)
}

/**
 * The ship origin that puts the hull box's centre at (x, y, z) at rotation `rot`, into `out`: the docked and launch
 * poses centre the hull box on the pad and the mouth as the autopilot does (D-TBD). Allocation-free.
 */
export function originForHullCentre(x: number, y: number, z: number, rot: Readonly<Quat>, c: Readonly<[number, number, number]>, out: Vec3): Vec3 {
  rotateVec(rot, c[0], c[1], c[2], out)
  out.x = x - out.x
  out.y = y - out.y
  out.z = z - out.z
  return out
}

/**
 * `originForHullCentre` with the hull centre's world point read from `at` (the same arithmetic, bit for bit; `out`
 * must not alias `at`). Only objects cross the calls: the per-tick cradle pin (P5a11b; D-1052's pattern).
 */
export function originForHullCentreFrom(at: Readonly<Vec3>, rot: Readonly<Quat>, c: Readonly<[number, number, number]>, out: Vec3): Vec3 {
  hullC.x = c[0]
  hullC.y = c[1]
  hullC.z = c[2]
  rotateVecFrom(rot, hullC, out)
  out.x = at.x - out.x
  out.y = at.y - out.y
  out.z = at.z - out.z
  return out
}

/** `hullHalfAlong` into `out.value` (bit for bit; objects only, DA-1). Allocation-free. */
export function hullHalfAlongTo(rot: Readonly<Quat>, half: Readonly<[number, number, number]>, d: Readonly<Vec3>, out: FloatScratch): void {
  inverseRotateFrom(rot, d, tmp)
  out.value = Math.abs(tmp.x) * half[0] + Math.abs(tmp.y) * half[1] + Math.abs(tmp.z) * half[2]
}

/**
 * Half the extent (m) along the unit world direction `d` of the hull box of half extents `half` (ship space, about
 * `hullCentre`) at rotation `rot`: Σ |d · axis_i| · half_i, the turned-box extent of the asset bayFit check.
 * Allocation-free.
 */
export function hullHalfAlong(rot: Readonly<Quat>, half: Readonly<[number, number, number]>, d: Readonly<Vec3>): number {
  hullHalfAlongTo(rot, half, d, result)
  return result.value
}

/**
 * The detour velocity round the clearance sphere (centre `c`, radius `k.clearance`) from `p` toward `entry`, at
 * `k.leg`, into `out`; the steering point into `target`. Works in the plane of the centre, the ship and the entry
 * point: outside the sphere it flies the tangent line to the sphere on the entry side, on or inside it the tangent
 * round the sphere plus an outward push of `k.gain × depth`. A ship dead astern (on the line through the centre and the
 * entry point) goes round over the bay's up side (the right side if that is degenerate).
 */
export function detourVelocityFrom(
  p: Readonly<Vec3>,
  c: Readonly<Vec3>,
  k: Readonly<AutodockFloats>,
  entry: Readonly<Vec3>,
  frame: Readonly<BayFrame>,
  out: Vec3,
  target: Vec3,
): Vec3 {
  const px = p.x
  const py = p.y
  const pz = p.z
  const radius = k.clearance
  const speed = k.leg
  const gain = k.gain
  // e: unit from the centre toward the entry point.
  let ex = entry.x - c.x
  let ey = entry.y - c.y
  let ez = entry.z - c.z
  const el = Math.sqrt(ex * ex + ey * ey + ez * ez)
  if (el < EPS) {
    ex = frame.out.x
    ey = frame.out.y
    ez = frame.out.z
  } else {
    ex /= el
    ey /= el
    ez /= el
  }
  const rx = px - c.x
  const ry = py - c.y
  const rz = pz - c.z
  const r = Math.sqrt(rx * rx + ry * ry + rz * rz)
  // s: unit toward the ship's side, perpendicular to e; theta: the ship's angle from e seen from the centre.
  const pe = rx * ex + ry * ey + rz * ez
  let sx = rx - pe * ex
  let sy = ry - pe * ey
  let sz = rz - pe * ez
  let sl = Math.sqrt(sx * sx + sy * sy + sz * sz)
  const theta = Math.atan2(sl, pe)
  if (sl < EPS * (r > 1 ? r : 1)) {
    // On the axis: go round over the bay's up side (its right side when up is along e).
    const u = frame.up
    const d = u.x * ex + u.y * ey + u.z * ez
    sx = u.x - d * ex
    sy = u.y - d * ey
    sz = u.z - d * ez
    sl = Math.sqrt(sx * sx + sy * sy + sz * sz)
    if (sl < EPS) {
      const w = frame.right
      const dr = w.x * ex + w.y * ey + w.z * ez
      sx = w.x - dr * ex
      sy = w.y - dr * ey
      sz = w.z - dr * ez
      sl = Math.sqrt(sx * sx + sy * sy + sz * sz)
    }
  }
  sx /= sl
  sy /= sl
  sz /= sl
  if (r > radius) {
    // Tangent point on the entry side: theta − acos(radius / r); the tangent line there heads for decreasing angle.
    let tt = theta - Math.acos(radius / r)
    if (tt < 0) tt = 0
    const sn = Math.sin(tt)
    const cs = Math.cos(tt)
    target.x = c.x + radius * (ex * cs + sx * sn)
    target.y = c.y + radius * (ey * cs + sy * sn)
    target.z = c.z + radius * (ez * cs + sz * sn)
    out.x = (ex * sn - sx * cs) * speed
    out.y = (ey * sn - sy * cs) * speed
    out.z = (ez * sn - sz * cs) * speed
    return out
  }
  // On or inside the sphere: round it at `speed` and out at gain × depth, the sum scaled to `speed`.
  const sn = Math.sin(theta)
  const cs = Math.cos(theta)
  const push = gain * (radius - r)
  const vx = (ex * sn - sx * cs) * speed + (ex * cs + sx * sn) * push
  const vy = (ey * sn - sy * cs) * speed + (ey * cs + sy * sn) * push
  const vz = (ez * sn - sz * cs) * speed + (ez * cs + sz * sn) * push
  const vl = Math.sqrt(vx * vx + vy * vy + vz * vz)
  const kk = vl > EPS ? speed / vl : 0
  out.x = vx * kk
  out.y = vy * kk
  out.z = vz * kk
  target.x = c.x + radius * (ex * cs + sx * sn)
  target.y = c.y + radius * (ey * cs + sy * sn)
  target.z = c.z + radius * (ez * cs + sz * sn)
  return out
}

/** `detourVelocityFrom` from the point (px, py, pz) with float radius, speed and gain (the same arithmetic). */
export function detourVelocity(
  px: number,
  py: number,
  pz: number,
  c: Readonly<Vec3>,
  radius: number,
  entry: Readonly<Vec3>,
  frame: Readonly<BayFrame>,
  speed: number,
  gain: number,
  out: Vec3,
  target: Vec3,
): Vec3 {
  wrapA.x = px
  wrapA.y = py
  wrapA.z = pz
  wrapFloats.clearance = radius
  wrapFloats.leg = speed
  wrapFloats.gain = gain
  return detourVelocityFrom(wrapA, c, wrapFloats, entry, frame, out, target)
}

/**
 * The speed cap of the detour and entry legs `near` metres from the entry point or the approach tube, whichever is
 * closer: `approachSpeed` within `detourNear`; beyond it, rising as `lateralGain` × the excess to `detourSpeed`, so
 * the brake the profile asks for stays at `gain × speed` as in every other profile here, and the ship is back at
 * `approachSpeed` before it reaches the tube. Scale build SC-5a1b: a far-side detour round a km station is ~πR,
 * 55 s at 90 m/s on a 3 km station, 25 s at 200.
 */
export function legSpeed(near: number, ap: Readonly<DockingConfig['autopilot']>): number {
  const excess = near - ap.detourNear
  if (!(excess > 0)) return ap.approachSpeed
  return clamp(ap.approachSpeed + ap.lateralGain * excess, ap.approachSpeed, ap.detourSpeed)
}

/** `out` = the direction from `p` to `to` × `speed(distance)` = clamp(`k.gain` × distance, `k.lo`, `k.hi`). */
function approachFrom(p: Readonly<Vec3>, to: Readonly<Vec3>, k: Readonly<AutodockFloats>, out: Vec3): Vec3 {
  const dx = to.x - p.x
  const dy = to.y - p.y
  const dz = to.z - p.z
  const d = Math.sqrt(dx * dx + dy * dy + dz * dz)
  if (d < EPS) {
    out.x = 0
    out.y = 0
    out.z = 0
    return out
  }
  const s = clamp(k.gain * d, k.lo, k.hi) / d
  out.x = dx * s
  out.y = dy * s
  out.z = dz * s
  return out
}

/**
 * The world nose (local −Z) into `fwd` and up (local +Y) into `up` of a ship at rotation `q`: `rotateRaw`'s expression
 * with the axis written in, bit for bit (`rotateVec(q, 0, 0, -1, fwd)`, `rotateVec(q, 0, 1, 0, up)`). Written out
 * because `rotateVecFrom` with the pooled axes still boxed 4 floats per tick in `steerAttitude` and the docking
 * runtime's `readShip` (DA-1, D-TBD: measured, not explained; the readings follow `rotateVecFrom`'s shared feedback).
 */
export function noseUpInto(q: Readonly<Quat>, fwd: Vec3, up: Vec3): void {
  const qx = q.x
  const qy = q.y
  const qz = q.z
  const qw = q.w
  let vx = 0
  let vy = 0
  let vz = -1
  let tx = 2 * (qy * vz - qz * vy)
  let ty = 2 * (qz * vx - qx * vz)
  let tz = 2 * (qx * vy - qy * vx)
  fwd.x = vx + qw * tx + (qy * tz - qz * ty)
  fwd.y = vy + qw * ty + (qz * tx - qx * tz)
  fwd.z = vz + qw * tz + (qx * ty - qy * tx)
  vx = 0
  vy = 1
  vz = 0
  tx = 2 * (qy * vz - qz * vy)
  ty = 2 * (qz * vx - qx * vz)
  tz = 2 * (qx * vy - qy * vx)
  up.x = vx + qw * tx + (qy * tz - qz * ty)
  up.y = vy + qw * ty + (qz * tx - qx * tz)
  up.z = vz + qw * tz + (qx * ty - qy * tx)
}

/**
 * Pitch and yaw toward the unit world direction `dir` and roll toward the bay's up, into `out`, at `k.turnGain` and
 * `k.rollGain`. The nose error is the rotation vector that turns the nose (local −Z) onto the direction; dead astern it
 * pitches. Leaves the ship's world nose and up in the module scratch (`stepAutodockAt`'s finish check reads them).
 */
export function steerAttitudeFrom(rot: Readonly<Quat>, dir: Readonly<Vec3>, frame: Readonly<BayFrame>, k: AutodockFloats, out: ControlInput): ControlInput {
  inverseRotateFrom(rot, dir, tmp)
  const lat = Math.sqrt(tmp.x * tmp.x + tmp.y * tmp.y)
  const ang = Math.atan2(lat, -tmp.z)
  if (lat > EPS) {
    // Axis = nose × direction = (0, 0, −1) × (x, y, z) = (y, −x, 0).
    k.wx = (tmp.y / lat) * ang
    k.wy = (-tmp.x / lat) * ang
  } else if (tmp.z > 0) {
    k.wx = Math.PI
    k.wy = 0
  } else {
    k.wx = 0
    k.wy = 0
  }
  // The clamps written out (`clamp`'s expression): past the inlining budget an un-inlined `clamp` boxes its argument
  // and its result (DA-1; D-1052's flight model).
  const pitch = k.turnGain * k.wx
  out.pitch = pitch < -1 ? -1 : pitch > 1 ? 1 : pitch
  // +yaw (nose right) is a negative rotation about local +Y.
  const yaw = -k.turnGain * k.wy
  out.yaw = yaw < -1 ? -1 : yaw > 1 ? 1 : yaw
  noseUpInto(rot, fwd, up)
  // rollError > 0 = rolled right of the bay's up: roll left.
  rollErrorTo(frame, fwd, up, result)
  const roll = -k.rollGain * result.value
  out.roll = roll < -1 ? -1 : roll > 1 ? 1 : roll
  return out
}

/** `steerAttitudeFrom` toward (fx, fy, fz) with float gains (the same arithmetic). */
export function steerAttitude(rot: Readonly<Quat>, fx: number, fy: number, fz: number, frame: Readonly<BayFrame>, turnGain: number, rollGain: number, out: ControlInput): ControlInput {
  wrapA.x = fx
  wrapA.y = fy
  wrapA.z = fz
  wrapFloats.turnGain = turnGain
  wrapFloats.rollGain = rollGain
  return steerAttitudeFrom(rot, wrapA, frame, wrapFloats, out)
}

/** Translation sticks for the world velocity `v` with FA on: ship-local velocity ÷ `k.maxSpeed`, clamped. */
export function velocityStickFrom(rot: Readonly<Quat>, v: Readonly<Vec3>, k: Readonly<AutodockFloats>, out: ControlInput): ControlInput {
  inverseRotateFrom(rot, v, tmp)
  const maxSpeed = k.maxSpeed
  out.surge = clamp(-tmp.z / maxSpeed, -1, 1)
  out.sway = clamp(tmp.x / maxSpeed, -1, 1)
  out.heave = clamp(tmp.y / maxSpeed, -1, 1)
  return out
}

/** Translation sticks for the world velocity `v` with FA on: ship-local velocity ÷ `maxSpeed`, clamped. */
export function velocityStick(rot: Readonly<Quat>, v: Readonly<Vec3>, maxSpeed: number, out: ControlInput): ControlInput {
  inverseRotateVec(rot, v.x, v.y, v.z, tmp)
  out.surge = clamp(-tmp.z / maxSpeed, -1, 1)
  out.sway = clamp(tmp.x / maxSpeed, -1, 1)
  out.heave = clamp(tmp.y / maxSpeed, -1, 1)
  return out
}

/**
 * A stick scratch with its own map (D-1052; P5a11b C5): a `{ surge, ... }` literal shares its map with every literal of
 * that shape game-wide, so a field can turn tagged, and then each of `velocityStick`'s float stores boxes (16 B).
 */
export class ControlScratch implements ControlInput {
  surge = 0
  sway = 0
  heave = 0
  pitch = 0
  yaw = 0
  roll = 0
}

/** All six axes to zero (FA then brings the ship to rest). */
export function zeroControl(out: ControlInput): ControlInput {
  out.surge = 0
  out.sway = 0
  out.heave = 0
  out.pitch = 0
  out.yaw = 0
  out.roll = 0
  return out
}

function finiteShip(ship: Readonly<AutodockShip>): boolean {
  const p = ship.pos
  const v = ship.vel
  const q = ship.rot
  return (
    Number.isFinite(p.x) &&
    Number.isFinite(p.y) &&
    Number.isFinite(p.z) &&
    Number.isFinite(v.x) &&
    Number.isFinite(v.y) &&
    Number.isFinite(v.z) &&
    Number.isFinite(q.x) &&
    Number.isFinite(q.y) &&
    Number.isFinite(q.z) &&
    Number.isFinite(q.w)
  )
}

/**
 * One autopilot step for a ship docking at the bay `frame` of `station` (its float64 world centre `pos` and the radius
 * bounding its collider): updates `state`, writes all six axes of `out` and returns the result (module doc). `dt` only
 * advances `state.elapsed`. Allocation-free: its callees take objects and the `AutodockFloats` scratch, never floats
 * (DA-1, D-TBD).
 *
 * `frameVel` (M10 P5a9b1, review F19) is the world velocity of a bay that moves (a holding L's cradle, rebuilt each
 * tick by the caller): every phase's desired velocity is then relative to the bay, the stick commands that plus
 * `frameVel`, and the finish speed limit (`dock.maxSpeed`) is on the ship's velocity relative to the bay. Omitted (a
 * station: static), the step is unchanged.
 */
export function stepAutodockAt(
  state: AutodockState,
  frame: Readonly<BayFrame>,
  station: Readonly<AutodockStation>,
  ship: Readonly<AutodockShip>,
  def: Readonly<AutodockDef>,
  cfg: Readonly<AutodockConfig>,
  dt: number,
  out: ControlInput,
  frameVel?: Readonly<Vec3>,
): AutodockResult {
  const k = floats
  const ap = cfg.autopilot
  const stationPos = station.pos
  const stationRadius = station.radius
  // The mouth less the margin to each wall, and what the hull leaves of it lined up with the bay (D-999, D-TBD).
  const roomW = frame.width * 0.5 - ap.hullMargin
  const roomH = frame.height * 0.5 - ap.hullMargin
  const half = def.hullHalfExtents
  if (
    !finiteShip(ship) ||
    !(roomW - half[0] >= 0) ||
    !(roomH - half[1] >= 0) ||
    !(half[2] * 2 <= frame.depth) ||
    !(stationRadius >= 0) ||
    !(def.maxSpeed > 0)
  ) {
    zeroControl(out)
    return 'failed'
  }
  state.elapsed += dt
  // Every phase flies the hull box's centre, not the ship origin, so the box is centred in the mouth and bay (D-TBD).
  const hc = hullCentreAt(ship.pos, ship.rot, def.hullCentre, centre)
  const gain = ap.lateralGain
  k.gain = gain
  k.clearance = stationRadius + ap.stationMargin
  const tube = corridorRadius(frame, cfg)
  entryAlongFrom(frame, stationPos, k, cfg)
  const entryAt = k.entryAt
  const tubeEnd = entryAt > cfg.corridor.length ? entryAt : cfg.corridor.length
  const e = state.entry
  e.x = frame.mouth.x + frame.out.x * entryAt
  e.y = frame.mouth.y + frame.out.y * entryAt
  e.z = frame.mouth.z + frame.out.z * entryAt
  bayLocalOf(frame, hc, loc)
  const along = loc.z - frame.depth * 0.5
  const lateral = Math.sqrt(loc.x * loc.x + loc.y * loc.y)
  const inBay = pointInBayOf(frame, hc)
  const ex = e.x - hc.x, ey = e.y - hc.y, ez = e.z - hc.z
  const toEntry = Math.sqrt(ex * ex + ey * ey + ez * ez) // not Math.hypot: it allocates per call in V8 (W2 gate fix-up)

  // Phases. The tube phases are sticky; a ship pushed well out of the tube starts over.
  let phase: AutodockPhase = state.phase
  if (phase === AUTODOCK_PHASE.corridor && (lateral > tube + ap.arriveRadius || along > tubeEnd + ap.arriveRadius)) {
    phase = AUTODOCK_PHASE.entry
  }
  if (phase === AUTODOCK_PHASE.detour || phase === AUTODOCK_PHASE.entry) {
    if (inBay) {
      phase = AUTODOCK_PHASE.bay
    } else if (
      (along >= 0 && along <= tubeEnd && lateral <= tube) ||
      toEntry <= ap.arriveRadius
    ) {
      phase = AUTODOCK_PHASE.corridor
    } else {
      phase = segmentBlockedFrom(hc, e, stationPos, k) ? AUTODOCK_PHASE.detour : AUTODOCK_PHASE.entry
    }
  }
  if (phase === AUTODOCK_PHASE.corridor && along <= 0) phase = AUTODOCK_PHASE.bay
  const pad = frame.pad
  const padx = pad.x - hc.x
  const pady = pad.y - hc.y
  const padz = pad.z - hc.z
  const padDistance = Math.sqrt(padx * padx + pady * pady + padz * padz)
  if (phase === AUTODOCK_PHASE.bay && padDistance <= ap.finalSpeed / gain) phase = AUTODOCK_PHASE.hold
  state.phase = phase

  // Desired world velocity.
  const v = state.desiredVel
  const t = state.target
  let leg = ap.approachSpeed
  if (phase <= AUTODOCK_PHASE.entry) {
    // Distance to the approach tube (along ∈ [0, tubeEnd], lateral ≤ tube) or the entry point, whichever is nearer.
    const outAlong = along > tubeEnd ? along - tubeEnd : along < 0 ? -along : 0
    const outLat = lateral > tube ? lateral - tube : 0
    const toTube = Math.sqrt(outAlong * outAlong + outLat * outLat)
    leg = legSpeed(toTube < toEntry ? toTube : toEntry, ap)
  }
  if (phase === AUTODOCK_PHASE.detour) {
    k.leg = leg
    detourVelocityFrom(hc, stationPos, k, e, frame, v, t)
  } else if (phase === AUTODOCK_PHASE.entry) {
    k.lo = 0
    k.hi = leg
    approachFrom(hc, e, k, v)
    t.x = e.x
    t.y = e.y
    t.z = e.z
  } else if (phase === AUTODOCK_PHASE.corridor) {
    // Inward speed toward the mouth, or toward a standoff in front of it until the hull, as it is turned now, fits the
    // mouth less the margin.
    hullHalfAlongTo(ship.rot, half, frame.right, result)
    let lined = Math.abs(loc.x) + result.value <= roomW
    if (lined) {
      hullHalfAlongTo(ship.rot, half, frame.up, result)
      lined = Math.abs(loc.y) + result.value <= roomH
    }
    const inward = lined
      ? clamp(gain * along, ap.baySpeed, ap.corridorSpeed)
      : clamp(gain * (along - ap.arriveRadius), -ap.baySpeed, ap.corridorSpeed)
    const o = frame.out
    const r = frame.right
    const u = frame.up
    v.x = -o.x * inward - gain * (r.x * loc.x + u.x * loc.y)
    v.y = -o.y * inward - gain * (r.y * loc.x + u.y * loc.y)
    v.z = -o.z * inward - gain * (r.z * loc.x + u.z * loc.y)
    t.x = frame.mouth.x
    t.y = frame.mouth.y
    t.z = frame.mouth.z
  } else if (phase === AUTODOCK_PHASE.bay) {
    k.lo = ap.finalSpeed
    k.hi = ap.baySpeed
    approachFrom(hc, pad, k, v)
    t.x = pad.x
    t.y = pad.y
    t.z = pad.z
  } else {
    k.lo = 0
    k.hi = ap.finalSpeed
    approachFrom(hc, pad, k, v)
    t.x = pad.x
    t.y = pad.y
    t.z = pad.z
  }

  // Nose along the desired velocity out in space, into the bay from the corridor on.
  const speed = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z)
  if (phase <= AUTODOCK_PHASE.entry && speed > ap.finalSpeed) {
    nose.x = v.x / speed
    nose.y = v.y / speed
    nose.z = v.z / speed
  } else {
    nose.x = -frame.out.x
    nose.y = -frame.out.y
    nose.z = -frame.out.z
  }
  k.turnGain = ap.turnGain
  k.rollGain = ap.rollGain
  steerAttitudeFrom(ship.rot, nose, frame, k, out)
  k.maxSpeed = def.maxSpeed
  if (frameVel === undefined) {
    velocityStickFrom(ship.rot, v, k, out)
  } else {
    worldVel.x = v.x + frameVel.x
    worldVel.y = v.y + frameVel.y
    worldVel.z = v.z + frameVel.z
    velocityStickFrom(ship.rot, worldVel, k, out)
  }

  if (phase < AUTODOCK_PHASE.bay || !inBay) return 'running'
  let vel: Readonly<Vec3> = ship.vel
  if (frameVel !== undefined) {
    worldVel.x = vel.x - frameVel.x
    worldVel.y = vel.y - frameVel.y
    worldVel.z = vel.z - frameVel.z
    vel = worldVel
  }
  const maxAlign = cfg.dock.maxAlign
  // steerAttitudeFrom left the ship's world nose and up in the scratch.
  if (!(Math.sqrt(vel.x * vel.x + vel.y * vel.y + vel.z * vel.z) < cfg.dock.maxSpeed)) return 'running'
  noseErrorTo(frame, fwd, result)
  if (!(result.value <= maxAlign)) return 'running'
  rollErrorTo(frame, fwd, up, result)
  return Math.abs(result.value) <= maxAlign ? 'arrived' : 'running'
}

/**
 * `stepAutodockAt` for a station given as its centre `stationPos` and bounding `stationRadius` (float64 world): the
 * same step, for callers off the player's per-tick path (traffic, tests).
 */
export function stepAutodock(
  state: AutodockState,
  frame: Readonly<BayFrame>,
  stationPos: Readonly<Vec3>,
  stationRadius: number,
  ship: Readonly<AutodockShip>,
  def: Readonly<AutodockDef>,
  cfg: Readonly<AutodockConfig>,
  dt: number,
  out: ControlInput,
  frameVel?: Readonly<Vec3>,
): AutodockResult {
  wrapStation.pos = stationPos
  wrapStation.radius = stationRadius
  return stepAutodockAt(state, frame, wrapStation, ship, def, cfg, dt, out, frameVel)
}
